import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Award, TrendingUp, User, LogOut, Zap, Sparkles, Trophy, Fingerprint, ChevronRight, Edit2, Calendar, Phone, Mail, Camera, Gift } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

// Level definitions
const LEVELS = [
  { level: 1, name: 'Starter', color: 'from-gray-400 to-gray-500', icon: '🌱', sticker: null },
  { level: 2, name: 'Saver', color: 'from-green-400 to-emerald-500', icon: '💰', sticker: '💰' },
  { level: 3, name: 'Pro', color: 'from-blue-400 to-cyan-500', icon: '⚡', sticker: '⚡' },
  { level: 4, name: 'Elite', color: 'from-purple-400 to-violet-500', icon: '💎', sticker: '💎' },
  { level: 5, name: 'Master', color: 'from-orange-400 to-amber-500', icon: '🔥', sticker: '🔥' },
  { level: 6, name: 'Legend', color: 'from-yellow-400 to-orange-500', icon: '👑', sticker: '👑' },
  { level: 7, name: 'Champion', color: 'from-pink-400 to-rose-500', icon: '🏆', sticker: '🏆' },
  { level: 8, name: 'Titan', color: 'from-indigo-400 to-purple-600', icon: '⭐', sticker: '⭐' },
  { level: 9, name: 'Mythic', color: 'from-cyan-400 to-blue-600', icon: '🌟', sticker: '🌟' },
  { level: 10, name: 'Immortal', color: 'from-amber-300 via-yellow-400 to-orange-500', icon: '✨', sticker: '✨' }
];

const ACHIEVEMENT_STICKERS = {
  'week_warrior': { name: 'Week Warrior', sticker: '🔥' },
  'diamond_hand': { name: 'Diamond Hand', sticker: '💎' },
  'star_saver': { name: 'Star Saver', sticker: '⭐' },
  'social_butterfly': { name: 'Social Butterfly', sticker: '🦋' },
  'big_spender': { name: 'Big Spender', sticker: '💸' },
  'team_player': { name: 'Team Player', sticker: '🤝' },
  'profile_complete': { name: 'Profile Complete', sticker: '✅' }
};

const Profile = ({ user: initialUser, onLogout, onUpdateUser }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: initialUser.name || '',
    dob: initialUser.dob || '',
    phone: initialUser.phone || ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const res = await axios.get(`${API}/users/${initialUser.id}`);
      setUser(res.data);
      setEditForm({
        name: res.data.name || '',
        dob: res.data.dob || '',
        phone: res.data.phone || ''
      });
      localStorage.setItem('user', JSON.stringify(res.data));
    } catch (error) {
      console.error('Failed to fetch user data');
    }
  };

  const handleSaveProfile = async () => {
    if (!editForm.name.trim() || editForm.name.length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(`${API}/users/${user.id}/profile`, {
        name: editForm.name.trim(),
        dob: editForm.dob || null,
        phone: editForm.phone || null
      });
      
      setUser(response.data.user);
      if (onUpdateUser) onUpdateUser(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setShowEditModal(false);
      toast.success('Profile updated! 🎉');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const currentLevel = LEVELS.find(l => l.level === user.level) || LEVELS[0];
  const nextLevel = LEVELS.find(l => l.level === user.level + 1);
  
  const getXpForLevel = (level) => {
    if (level <= 1) return 0;
    if (level === 2) return 100;
    if (level === 3) return 300;
    if (level === 4) return 600;
    if (level === 5) return 1000;
    return 1500 + (level - 6) * 500;
  };
  
  const currentLevelXp = getXpForLevel(user.level);
  const nextLevelXp = getXpForLevel(user.level + 1);
  const progressXp = user.xp - currentLevelXp;
  const neededXp = nextLevelXp - currentLevelXp;
  const progressPercent = neededXp > 0 ? Math.min((progressXp / neededXp) * 100, 100) : 100;

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      {/* SEO */}
      <Helmet>
        <title>Profile | SharePay - Your Savings Journey</title>
        <meta name="description" content="View and manage your SharePay profile, track your XP progress, achievements, badges, and savings stats." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <div className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowEditModal(true)}
            className="rounded-full"
            data-testid="edit-profile-btn"
          >
            <Edit2 className="w-4 h-4 mr-2" /> Edit Profile
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          {/* Avatar */}
          <div className="relative inline-block mb-4">
            <div className={`w-28 h-28 rounded-full bg-gradient-to-br ${currentLevel.color} flex items-center justify-center text-5xl shadow-lg`}>
              {user.avatar_url ? (
                <img src={user.avatar_url} alt={user.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                currentLevel.icon
              )}
            </div>
            <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-violet-600 flex items-center justify-center text-white font-bold border-4 border-white dark:border-slate-900">
              {user.level}
            </div>
          </div>

          <h1 className="text-2xl font-bold">{user.name}</h1>
          <p className={`text-lg font-semibold bg-gradient-to-r ${currentLevel.color} bg-clip-text text-transparent`}>
            {currentLevel.name}
          </p>
          <p className="text-muted-foreground text-sm mt-1">{user.email}</p>
        </motion.div>

        {/* Profile Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-card rounded-2xl p-5 border border-border"
          >
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Personal Info</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">{user.email}</span>
                {user.email_verified && <span className="text-xs text-emerald-600">✓ Verified</span>}
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">{formatDate(user.dob)}</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">{user.phone ? `+91 ${user.phone}` : 'Not set'}</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-card rounded-2xl p-5 border border-border"
          >
            <h3 className="text-sm font-medium text-muted-foreground mb-3">Stats</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-bold text-violet-600">{user.xp || 0}</p>
                <p className="text-xs text-muted-foreground">Total XP</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">{user.reward_points || 0}</p>
                <p className="text-xs text-muted-foreground">Points</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">{user.contribution_streak || 0}</p>
                <p className="text-xs text-muted-foreground">Day Streak</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{user.total_contributions || 0}</p>
                <p className="text-xs text-muted-foreground">Contributions</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* XP Progress */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card rounded-2xl p-6 border border-border mb-8"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              <span className="font-semibold">Level Progress</span>
            </div>
            {nextLevel && (
              <span className="text-sm text-muted-foreground">
                {progressXp}/{neededXp} XP to Level {user.level + 1}
              </span>
            )}
          </div>
          <div className="w-full h-4 bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ duration: 1, delay: 0.5 }}
              className={`h-full bg-gradient-to-r ${currentLevel.color}`}
            />
          </div>
          {nextLevel && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Reach {nextLevel.name} and unlock {nextLevel.sticker}
            </p>
          )}
        </motion.div>

        {/* Stickers Collection */}
        {(user.stickers?.length > 0 || user.badges?.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="bg-card rounded-2xl p-6 border border-border mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-violet-500" />
              <span className="font-semibold">Sticker Collection</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {user.stickers?.map((sticker, i) => (
                <div key={i} className="text-3xl p-2 bg-muted rounded-xl hover:scale-110 transition-transform">
                  {sticker}
                </div>
              ))}
              {user.badges?.map((badge, i) => (
                <div key={`badge-${i}`} className="text-3xl p-2 bg-muted rounded-xl hover:scale-110 transition-transform" title={ACHIEVEMENT_STICKERS[badge]?.name}>
                  {ACHIEVEMENT_STICKERS[badge]?.sticker || '🎖️'}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-3"
        >
          <Button
            onClick={() => navigate('/referral')}
            variant="outline"
            className="w-full justify-between rounded-xl py-6"
          >
            <span className="flex items-center gap-2">
              <Gift className="w-5 h-5 text-violet-500" />
              Invite Friends & Earn
            </span>
            <ChevronRight className="w-5 h-5" />
          </Button>

          <Button
            onClick={() => navigate('/gamification')}
            variant="outline"
            className="w-full justify-between rounded-xl py-6"
          >
            <span className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              View All Achievements
            </span>
            <ChevronRight className="w-5 h-5" />
          </Button>

          <Button
            onClick={() => navigate('/biometric-settings')}
            variant="outline"
            className="w-full justify-between rounded-xl py-6"
          >
            <span className="flex items-center gap-2">
              <Fingerprint className="w-5 h-5 text-emerald-500" />
              Biometric Settings
            </span>
            <ChevronRight className="w-5 h-5" />
          </Button>

          <Button
            onClick={onLogout}
            variant="outline"
            className="w-full justify-between rounded-xl py-6 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20"
            data-testid="logout-btn"
          >
            <span className="flex items-center gap-2">
              <LogOut className="w-5 h-5" />
              Sign Out
            </span>
          </Button>
        </motion.div>
      </div>

      {/* Edit Profile Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                placeholder="Your name"
                className="mt-2 rounded-xl"
                data-testid="edit-name-input"
              />
            </div>

            <div>
              <Label htmlFor="edit-dob">Date of Birth</Label>
              <Input
                id="edit-dob"
                type="date"
                value={editForm.dob}
                onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })}
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                className="mt-2 rounded-xl"
                data-testid="edit-dob-input"
              />
            </div>

            <div>
              <Label htmlFor="edit-phone">Mobile Number (Indian only)</Label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-muted-foreground">+91</span>
                <Input
                  id="edit-phone"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  placeholder="9876543210"
                  className="rounded-xl"
                  data-testid="edit-phone-input"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 rounded-full gradient-primary"
                onClick={handleSaveProfile}
                disabled={saving}
                data-testid="save-profile-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
